/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 * @author Evgeniy Naumov
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.22.0
 */

import { pageApi } from '@/main/api/page.api';
import { API_REPORTS_CONSTRUCTOR_META, API_REPORTS_CONSTRUCTOR_TEMPLATES, DELETE, REPORTS_DASHBOARD, SCHEDULE, TEMPLATES_DASHBOARD, UPDATE } from '@modules/app/Api';
import { EHttpMethod } from '@modules/app/appConstants';

import { TEMPLATES_LOAD_SIZE } from './constants';
import { setTemplatesData } from './slice';
import type {
    IReportsConstructorMetaResponse,
    IReportsConstructorMetaQuery,
    ITemplateRenameUpdateRequest,
    ITemplateRemoveRequest,
    TReportId,
    IReportsConstructorTemplateDataResponse,
} from './types';

export const reportConstructorApi = pageApi.injectEndpoints({
    endpoints: (builder) => ({
        getReportConstructorMeta: builder.query<IReportsConstructorMetaResponse, IReportsConstructorMetaQuery>({
            query: ({ source, bisAccountId }) => ({
                url: API_REPORTS_CONSTRUCTOR_META,
                params: { bisAccountId, source },
            }),
            // Не кешировать
            keepUnusedDataFor: 0,
        }),
        getConstructorTemplates: builder.query<IReportsConstructorTemplateDataResponse, number>({
            query: (page) => ({
                url: API_REPORTS_CONSTRUCTOR_TEMPLATES,
                params: { from: page === 0 ? page : TEMPLATES_LOAD_SIZE * page, size: TEMPLATES_LOAD_SIZE },
            }),
            // Не кешировать
            keepUnusedDataFor: 0,
            async onQueryStarted(page, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    const { elements, count } = data;
                    /* Данные хранятся в slice */
                    dispatch(
                        setTemplatesData({
                            elements: elements || [],
                            count,
                            page,
                        }),
                    );
                } catch (err) {
                    console.error(err);
                }
            },
        }),
        removeSchedule: builder.mutation<void, { cppoId: number; reportId: TReportId }>({
            query: ({ cppoId, reportId }) => ({
                url: `${REPORTS_DASHBOARD}/${reportId}/${SCHEDULE}/${cppoId}/${DELETE}`,
                method: EHttpMethod.DELETE,
            }),
        }),
        updateReportTemplateName: builder.mutation<string, ITemplateRenameUpdateRequest>({
            query: ({ templateId, name }) => ({
                url: `${TEMPLATES_DASHBOARD}/${templateId}/${UPDATE}`,
                method: EHttpMethod.PUT,
                body: { name },
            }),
            transformResponse: (_res, _meta, arg) => arg.name,
        }),
        removeReportTemplate: builder.mutation<void, ITemplateRemoveRequest>({
            query: ({ templateId }) => ({
                url: `${TEMPLATES_DASHBOARD}/${templateId}/${DELETE}`,
                method: EHttpMethod.DELETE,
            }),
        }),
    }),
});

export const {
    useGetReportConstructorMetaQuery,
    useLazyGetConstructorTemplatesQuery,
    useUpdateReportTemplateNameMutation,
    useRemoveReportTemplateMutation,
    useRemoveScheduleMutation,
} = reportConstructorApi;
